<script setup>
import PageWrapper from '@/components/shared/page/PageWrapper.vue'
import PageSection from '@/components/shared/page/PageSection.vue'
import PageImage from '@/components/shared/page/PageImage.vue'
import Heading from '@/components/shared/page/Heading.vue'
import SideBySide from '@/components/shared/page/SideBySide.vue'
import AddressDisplay from '@/components/shared/transform/AddressDisplay.vue'
import DateTime from '@/components/shared/transform/DateTime.vue'
import Markdown from '@/components/shared/transform/Markdown.vue'
import FormField from '@/components/shared/form/FormField.vue'
import TimeInput from '@/components/shared/form/TimeInput.vue'
import TimezoneSelectInput from '@/components/shared/form/TimezoneSelectInput.vue'
import Timezone from '@/components/shared/transform/Timezone.vue'
import Spinner from '@/components/shared/core/Spinner.vue'
import ActionButton from '@/components/shared/buttons/ActionButton.vue'
import EnrollmentActions from '@/components/enrollments/EnrollmentActions.vue'
import EnrollmentBadge from '@/components/enrollments/EnrollmentBadge.vue'
import {myEnrollmentsApi} from '@/api/my-enrollments.api.js'
import {dateFormats} from '@/constants/date-and-time.js'
import {truncate} from '@/helpers/string.js'
import {useRouting} from '@/composables/use-routing.js'
import {useNavigation} from '@/composables/use-navigation.js'
import {useErrorHandling} from '@/composables/use-error-handling.js'
import {useCurrentOrganization} from '@/composables/use-current-organization.js'
import {useEnrollmentEditing} from '@/composables/use-enrollment-editing.js'
import {useEnrollmentManagement} from '@/composables/use-enrollment-management.js'
import {ref, computed, onMounted} from 'vue'

//Error handler and routing
const {processError} = useErrorHandling()
const {routeParams, replaceRoute} = useRouting()
const {enrollmentId} = routeParams.value

//Navigation
const {allowUsersToManageEnrollments} = useCurrentOrganization()
const {goToHome, goToConversationForEnrollment} = useNavigation()

//Refs
const meta = ref(null)
const isLoadingEnrollment = ref(true)
const enrollment = ref(null)
const course = computed(() => enrollment.value?.course)

//Description truncation
const maxDescriptionLength = 170
const showFullDescription = ref(false)
const canShowFullDescription = computed(() => course.value?.description?.length > maxDescriptionLength)
const description = computed(() => {
  if (showFullDescription.value) {
    return course.value?.description
  }
  return truncate(course.value?.description, maxDescriptionLength)
})

//Load enrollment
const loadEnrollment = async () => {
  try {
    const data = await myEnrollmentsApi.findById(enrollmentId)
    enrollment.value = data.enrollment
    meta.value = data.meta
  }
  finally {
    isLoadingEnrollment.value = false
  }
}

//Enrollment editing
const {
  model,
  updateModel,
  updateEnrollment,
} = useEnrollmentEditing(enrollment)

//Enrollment management
const {
  updateIsUnread,
  startOrStopEnrollment,
  isStartingOrStoppingEnrollment,
} = useEnrollmentManagement(enrollment)

//Helper wrapper to mark the enrollment as unread
//After marking as unread, we redirect to the home view. We deliberately don't
//go to the conversation view here, as that would mark the enrollment as read
//again on mount.
const markAsUnread = async () => {
  try {
    await updateIsUnread(true)
    goToHome()
  }
  catch {
    //Ignore
  }
}

//Load on mount
onMounted(async () => {
  if (!allowUsersToManageEnrollments.value) {
    replaceRoute('home')
    return
  }
  try {
    await loadEnrollment()
  }
  catch (error) {
    processError(error)
    replaceRoute('home')
  }
})
</script>
<template>
  <PageWrapper
    header="Manage enrollment"
    can-go-back
    @back="goToConversationForEnrollment(enrollment)"
  >
    <template #actions>
      <EnrollmentActions
        v-if="enrollment && !enrollment.hasEnded"
        :can-toggle-unread="!enrollment.hasEnded"
        :can-toggle-paused="enrollment.canPauseResume"
        :is-unread="enrollment.isUnread"
        :is-paused="!!enrollment.pausedAt"
        @toggle-unread="markAsUnread()"
        @toggle-paused="startOrStopEnrollment()"
      />
    </template>
    <Spinner v-if="isLoadingEnrollment" />

    <template v-else-if="enrollment && course">
      <PageSection header="Content">
        <Heading size="s">
          {{ enrollment.displayName }}
        </Heading>

        <PageImage
          :image-url="course.signedImageUrl"
          aria-label="Course image"
        />
        <Markdown :content="description" />

        <ActionButton
          v-if="canShowFullDescription && !showFullDescription"
          :class="$style.showMore"
          type="link"
          label="Show more"
          aria-label="Show full description"
          @click="showFullDescription = true"
        />
      </PageSection>

      <PageSection header="Enrollment details">
        <SideBySide>
          <FormField label="Delivery method">
            <AddressDisplay
              :address="enrollment.address"
              show-type
            />
          </FormField>
          <FormField
            v-if="!enrollment.isBinge"
            label="Schedule"
          >
            <p>{{ enrollment.deliveryDays }}</p>
          </FormField>
        </SideBySide>
        <SideBySide>
          <FormField label="Delivery time">
            <p v-if="enrollment.isBinge">
              Instant
            </p>
            <p v-else-if="!meta.isTimeChangeable">
              <DateTime
                :date="enrollment.expectedDeliveryTime"
                :format="dateFormats.DATE_AND_TIME"
              />
            </p>
            <TimeInput
              v-else
              :value="model.enrollmentTime"
              aria-label="Delivery time"
              @input="updateModel({enrollmentTime: $event.value})"
              @close="updateEnrollment()"
            />
          </FormField>
          <FormField label="Timezone">
            <TimezoneSelectInput
              v-if="meta.isTimezoneChangeable && !enrollment.isBinge && enrollment.isActive"
              :value="model.timezone"
              test-id="timezone"
              @input="updateModel({timezone: $event.value})"
              @dirty="updateEnrollment()"
            />
            <Timezone
              v-else
              :timezone="enrollment.effectiveTimezone"
              empty-label="Not selected"
            />
          </FormField>
        </SideBySide>

        <FormField>
          <EnrollmentBadge :enrollment="enrollment" />
        </FormField>

        <FormField>
          <ActionButton
            v-if="enrollment.canPauseResume"
            type="secondary"
            :label="enrollment.pausedAt ? 'Resume' : 'Pause'"
            :icon="enrollment.pausedAt ? 'play_arrow' : 'pause'"
            is-icon-outlined
            :is-disabled="!enrollment.isResumable || isStartingOrStoppingEnrollment"
            :has-spinner="isStartingOrStoppingEnrollment"
            @click="startOrStopEnrollment(enrollment)"
          />
        </FormField>
      </PageSection>
    </template>
  </PageWrapper>
</template>

<style module>
.image {
  background-position: center center;
  height: 350px;
  background-repeat: no-repeat;
  background-size: contain;
  border-radius: var(--border-radius-m);
}
.section {
  &.half {
    width: 50%;
  }
  &:not(:first-child) {
    margin-top: 2rem;
  }
  p {
    margin-top: 0;
  }
}
.progress {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.showMore {
  margin-top: 0.5rem;
}
</style>
